import matter from 'gray-matter';
import { marked } from 'marked';
import { Buffer } from 'buffer';

// Make Buffer available globally for markdown processing
window.Buffer = Buffer;

// Extend marked renderer to handle video embeds
const renderer = new marked.Renderer();

// Handle YouTube embeds
renderer.link = function(href: string, title: string, text: string) {
  if (href?.includes('youtube.com/watch') || href?.includes('youtu.be/')) {
    const videoId = href.includes('youtube.com/watch') 
      ? new URL(href).searchParams.get('v')
      : href.split('/').pop();
    
    return `<div class="aspect-w-16 aspect-h-9 my-8">
      <iframe 
        src="https://www.youtube.com/embed/${videoId}"
        title="${title || text || 'YouTube video player'}"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
        class="w-full h-full rounded-lg"
      ></iframe>
    </div>`;
  }

  // Handle Vimeo embeds
  if (href?.includes('vimeo.com')) {
    const videoId = href.split('/').pop();
    return `<div class="aspect-w-16 aspect-h-9 my-8">
      <iframe 
        src="https://player.vimeo.com/video/${videoId}"
        title="${title || text || 'Vimeo video player'}"
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
        class="w-full h-full rounded-lg"
      ></iframe>
    </div>`;
  }

  // Check if link is external
  const isExternal = href?.startsWith('http') || href?.startsWith('https');
  
  return `<a 
    href="${href}" 
    ${isExternal ? 'target="_blank" rel="noopener noreferrer"' : ''} 
    title="${title || ''}"
  >${text}</a>`;
};

// Handle images with size attributes
renderer.image = function(href: string, title: string, text: string) {
  if (href?.match(/\.(mp4|webm|ogg)$/i)) {
    return `<div class="my-8">
      <video 
        controls
        class="w-full rounded-lg"
        ${title ? `title="${title}"` : ''}
      >
        <source src="${href}" type="video/${href.split('.').pop()}" />
        ${text ? `<p>${text}</p>` : ''}
      </video>
    </div>`;
  }
  
  // Parse size attributes from title if present
  // Format: "Title | width=80%" or "Title | width=500px"
  let imageTitle = title || '';
  let width: string | undefined;
  let height: string | undefined;
  
  if (title?.includes('|')) {
    const [titlePart, sizePart] = title.split('|').map(s => s.trim());
    imageTitle = titlePart;
    
    const sizeAttrs = sizePart.split(',').map(attr => attr.trim());
    sizeAttrs.forEach(attr => {
      const [key, value] = attr.split('=').map(s => s.trim());
      if (key === 'width') width = value;
      if (key === 'height') height = value;
    });
  }

  // Handle both percentage and pixel values
  const sizeStyles = [];
  if (width) {
    // If width is just a number or ends with px, add px
    if (/^\d+$/.test(width) || width.endsWith('px')) {
      sizeStyles.push(`width: ${width.endsWith('px') ? width : `${width}px`}`);
    } else if (width.endsWith('%')) {
      sizeStyles.push(`width: ${width}`);
    }
  }
  if (height) {
    if (/^\d+$/.test(height) || height.endsWith('px')) {
      sizeStyles.push(`height: ${height.endsWith('px') ? height : `${height}px`}`);
    } else if (height.endsWith('%')) {
      sizeStyles.push(`height: ${height}`);
    }
  }
  
  return `<img 
    src="${href}" 
    alt="${text}"
    ${imageTitle ? `title="${imageTitle}"` : ''}
    ${sizeStyles.length ? `style="${sizeStyles.join('; ')}"` : ''}
    class="rounded-lg mx-auto"
  />`;
};

marked.setOptions({ renderer });

// Type definitions for blog posts
export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  category: string;
  excerpt: string;
  image: string;
  aiGeneratedImage?: boolean;
  content?: string;
  metaTags?: {
    title: string;
    description: string;
    ogImage: string;
  };
}

// Configuration interface for blog settings
interface BlogConfig {
  defaultImage: string;
  availablePosts_ja: string[];
  availablePosts_de: string[];
}

/**
 * Loads blog configuration from JSON file
 * Contains default image and available posts for each language
 */
async function loadConfig(): Promise<BlogConfig> {
  try {
    const response = await fetch('/content/blog/posts.json');
    if (!response.ok) {
      throw new Error(`Failed to load blog configuration: ${response.status} ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading blog configuration:', error);
    throw error;
  }
}

/**
 * Validates if a post exists in the available posts list
 * @param slug - Post slug to validate
 * @param language - Language code (ja/de)
 * @param config - Blog configuration
 */
async function validatePostExists(slug: string, language: string, config: BlogConfig): Promise<boolean> {
  const availablePosts = language === 'de' ? config.availablePosts_de : config.availablePosts_ja;
  return availablePosts.includes(slug);
}

/**
 * Loads markdown content for a specific blog post
 * @param slug - Unique identifier for the blog post
 * @param language - Language code (ja/de)
 */
async function loadMarkdownFile(slug: string, language: string): Promise<string> {
  try {
    const path = language === 'de' 
      ? `/content/blog/de/${slug}.md`
      : `/content/blog/ja/${slug}.md`;
    
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to load markdown file: ${path}`);
    }
    const text = await response.text();
    if (!text.trim()) {
      throw new Error(`Empty markdown file: ${path}`);
    }
    return text;
  } catch (error) {
    console.error(`Error loading markdown file: ${slug}`, error);
    return '';
  }
}

/**
 * Checks if an image exists at the given path
 * @param imagePath - URL or path to the image
 */
async function checkImageExists(imagePath: string): Promise<boolean> {
  if (imagePath.startsWith('http')) {
    return true;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = imagePath;
  });
}

/**
 * Gets the appropriate image URL, falling back to default if necessary
 * @param localImage - Local image path or URL
 * @param defaultImage - Default image URL to use as fallback
 */
async function getImageUrl(localImage: string | undefined, defaultImage: string): Promise<string> {
  // If no image specified, return default
  if (!localImage) {
    return defaultImage;
  }

  // If it's already an external URL, return as is
  if (localImage.startsWith('http')) {
    return localImage;
  }

  // Construct the full path for local image
  const fullPath = `/content/blog/images/${localImage}`;
  
  try {
    const exists = await checkImageExists(fullPath);
    if (!exists) {
      console.warn(`Image not found: ${fullPath}, using default: ${defaultImage}`);
      return defaultImage;
    }
    return fullPath;
  } catch (error) {
    console.error('Error checking image:', error);
    return defaultImage;
  }
}

/**
 * Retrieves all blog posts for a given language
 * @param language - Language code (ja/de)
 */
export async function getAllPosts(language: string = 'ja'): Promise<BlogPost[]> {
  const posts: BlogPost[] = [];
  let config: BlogConfig;
  
  try {
    config = await loadConfig();
  } catch (error) {
    console.error('Failed to load blog configuration:', error);
    return [];
  }
  
  // Get the correct posts array based on language
  const availablePosts = language === 'de' ? config.availablePosts_de : config.availablePosts_ja;

  if (!Array.isArray(availablePosts)) {
    console.error(`No posts available for language: ${language}`);
    return [];
  }

  const postPromises = availablePosts.map(async (slug) => {
    try {
      const content = await loadMarkdownFile(slug, language);
      if (!content) return null;
      
      const { data } = matter(content);
      
      // Get the image URL, using the local image path if it exists
      const imageUrl = await getImageUrl(data.image, config.defaultImage);
      
      // Use the same image for meta tags
      const ogImageUrl = imageUrl;
      
      return {
        slug,
        title: data.title || 'Untitled',
        date: data.date || new Date().toISOString(),
        category: data.category || 'Uncategorized',
        excerpt: data.excerpt || '',
        image: imageUrl,
        aiGeneratedImage: data.ai_generated_image === true,
        metaTags: {
          title: data.title || 'Untitled',
          description: data.excerpt || '',
          ogImage: ogImageUrl
        }
      };
    } catch (error) {
      console.error(`Error processing post: ${slug}`, error);
      return null;
    }
  });

  const results = await Promise.all(postPromises);
  const validPosts = results.filter((post): post is BlogPost => post !== null);

  return validPosts.sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

/**
 * Retrieves a single blog post by its slug
 * @param slug - Unique identifier for the blog post
 * @param language - Language code (ja/de)
 */
export async function getPostBySlug(slug: string, language: string = 'ja'): Promise<BlogPost | null> {
  try {
    const config = await loadConfig();
    
    // First validate if the post exists in our configuration
    const exists = await validatePostExists(slug, language, config);
    if (!exists) {
      console.warn(`Post not found in configuration: ${slug}`);
      return null;
    }

    const content = await loadMarkdownFile(slug, language);
    if (!content) {
      console.warn(`No content found for post: ${slug}`);
      return null;
    }
    
    const { data, content: markdownContent } = matter(content);
    if (!data || !markdownContent) {
      console.warn(`Invalid markdown format for post: ${slug}`);
      return null;
    }

    const htmlContent = marked(markdownContent);
    
    // Get the image URL, using the local image path if it exists
    const imageUrl = await getImageUrl(data.image, config.defaultImage);
    
    // Use the same image for meta tags
    const ogImageUrl = imageUrl;

    return {
      slug,
      title: data.title || 'Untitled',
      date: data.date || new Date().toISOString(),
      category: data.category || 'Uncategorized',
      excerpt: data.excerpt || '',
      image: imageUrl,
      aiGeneratedImage: data.ai_generated_image === true,
      content: htmlContent,
      metaTags: {
        title: data.title || 'Untitled',
        description: data.excerpt || '',
        ogImage: ogImageUrl
      }
    };
  } catch (error) {
    console.error(`Error loading post: ${slug}`, error);
    return null;
  }
}